import { useRef, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UploadSimple, LinkSimple, Spinner, FileArrowUp } from "@phosphor-icons/react";

const ALLOWED_EXT = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
  ".txt", ".rtf", ".md", ".csv", ".tsv",
  ".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg", ".tiff",
  ".zip", ".7z", ".tar", ".gz", ".eml", ".msg", ".vsd", ".vsdx",
  ".xml", ".json", ".log", ".dwg", ".dxf",
];
const ACCEPT_ATTR = ALLOWED_EXT.join(",");
const MAX_BYTES = 50 * 1024 * 1024; // matches backend EVIDENCE_MAX_BYTES
const READABLE_EXT = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt", ".csv", ".tsv", ".md", ".json", ".xml", ".log"];
const extOf = (name) => { const i = (name || "").lastIndexOf("."); return i >= 0 ? name.slice(i).toLowerCase() : ""; };

// Reusable drag-and-drop evidence uploader for the RSAW development area.
// Stores files (or an external repository link) as Evidence linked to a requirement
// and, optionally, specific requirement part(s). Reuses the existing POST /evidence API.
export default function EvidenceDropzone({
  requirementId,
  parts = [],
  onUploaded,
  disabled = false,
  compact = false,
  testid = "evidence-dropzone",
}) {
  const inputRef = useRef(null);
  const { aiEnabled } = useAuth();
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");
  const [selParts, setSelParts] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");

  // Reset the part selection whenever the target requirement changes so a stale
  // part label is never silently attached to a different requirement.
  useEffect(() => { setSelParts([]); }, [requirementId]);

  const partLabels = (parts || []).map((p) => p.label).filter(Boolean);
  const togglePart = (label) =>
    setSelParts((cur) => (cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label]));

  const linkedPartsOf = (labels) =>
    (labels || []).map((label) => ({ requirement_id: requirementId, label }));

  const uploadFiles = useCallback(
    async (files) => {
      if (!requirementId || files.length === 0) return;
      const valid = [];
      for (const file of files) {
        const ext = extOf(file.name);
        if (!ALLOWED_EXT.includes(ext)) {
          toast.error(`${file.name}: file type ${ext || "(unknown)"} is not permitted`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          toast.error(`${file.name}: exceeds the 50MB limit`);
          continue;
        }
        valid.push(file);
      }
      if (valid.length === 0) return;
      setBusy(true);
      let ok = 0;
      const autoTicked = new Set();
      for (const file of valid) {
        let fileParts = [...selParts];
        // AI auto-link: read the file and suggest which part(s) it supports.
        if (aiEnabled && READABLE_EXT.includes(extOf(file.name))) {
          try {
            setPhase("Analyzing…");
            const afd = new FormData();
            afd.append("requirement_id", requirementId);
            afd.append("file", file);
            const { data: s } = await api.post("/evidence/suggest-links", afd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            if (s && s.readable) {
              (s.parts || []).forEach((p) => {
                if (!fileParts.includes(p)) fileParts.push(p);
                autoTicked.add(p);
              });
              if ((s.parts || []).length) {
                toast.success(`AI linked ${file.name} to part(s) ${s.parts.join(", ")}`);
              }
              if ((s.other_requirements || []).length) {
                const reqs = s.other_requirements.map((o) => o.req_number).join(", ");
                toast.warning(`Heads up: ${file.name} may belong to ${reqs} instead of this requirement`);
              }
              if (!(s.parts || []).length && !(s.other_requirements || []).length) {
                toast.info(`AI found no specific part for ${file.name} — set part(s) manually if needed`);
              }
            } else if (s && s.note) {
              toast.info(`${file.name}: ${s.note}`);
            }
          } catch (e) {
            // AI is best-effort; fall back to manual part selection.
          }
        }
        setPhase("Uploading…");
        const fd = new FormData();
        fd.append("name", file.name);
        fd.append("evidence_type", "Document");
        fd.append("linked_requirements", JSON.stringify([requirementId]));
        fd.append("linked_parts", JSON.stringify(linkedPartsOf(fileParts)));
        fd.append("source_type", "upload");
        fd.append("file", file);
        try {
          await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok += 1;
        } catch (e) {
          toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail)}`);
        }
      }
      setBusy(false);
      setPhase("");
      if (autoTicked.size) setSelParts((cur) => Array.from(new Set([...cur, ...autoTicked])));
      if (ok > 0) {
        toast.success(`${ok} file${ok > 1 ? "s" : ""} attached as evidence`);
        onUploaded && onUploaded();
      }
    },
    [requirementId, selParts, aiEnabled] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const submitLink = async () => {
    const url = linkUrl.trim();
    if (!/^https?:\/\//i.test(url)) return toast.error("Link must start with http:// or https://");
    setBusy(true);
    const fd = new FormData();
    fd.append("name", linkName.trim() || url);
    fd.append("evidence_type", "Link");
    fd.append("linked_requirements", JSON.stringify([requirementId]));
    fd.append("linked_parts", JSON.stringify(linkedPartsOf(selParts)));
    fd.append("source_type", "link");
    fd.append("link_url", url);
    try {
      await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Repository link attached as evidence");
      setLinkUrl(""); setLinkName(""); setShowLink(false); setSelParts([]);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || busy) return;
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  if (disabled) return null;

  return (
    <div className="space-y-2" data-testid={testid}>
      {partLabels.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="overline text-muted-foreground mr-1">Supports part(s):</span>
          {partLabels.map((label) => (
            <button
              key={label}
              type="button"
              data-testid={`${testid}-part-${label}`}
              onClick={() => togglePart(label)}
              className={`text-[11px] font-mono font-semibold px-2 py-0.5 border transition-colors ${
                selParts.includes(label)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border hover:border-primary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div
        role="button"
        tabIndex={0}
        data-testid={`${testid}-zone`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center text-center border-2 border-dashed cursor-pointer transition-colors ${
          compact ? "px-3 py-4" : "px-4 py-7"
        } ${dragOver ? "border-primary bg-primary/5" : "border-border bg-secondary/20 hover:border-primary/60"}`}
      >
        {busy ? (
          <Spinner size={compact ? 18 : 24} className="animate-spin text-primary" />
        ) : dragOver ? (
          <FileArrowUp size={compact ? 18 : 24} className="text-primary" />
        ) : (
          <UploadSimple size={compact ? 18 : 24} className="text-muted-foreground" />
        )}
        <p className={`mt-1.5 font-semibold ${compact ? "text-xs" : "text-sm"}`}>
          {busy ? (phase || "Uploading…") : "Drag & drop evidence here"}
        </p>
        {compact ? (
          <p className="text-[11px] text-muted-foreground mt-0.5">PDF, Word, Excel, images · 50MB{aiEnabled ? " · AI auto-links parts" : ""}</p>
        ) : (
          <p className="text-xs text-muted-foreground mt-0.5">
            or click to browse — PDF, Word, Excel, images (up to 50MB each){aiEnabled ? " · AI suggests the part(s) it supports" : ""}
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="hidden"
          data-testid={`${testid}-input`}
          onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
        />
      </div>

      {showLink ? (
        <div className="border border-border bg-card p-2.5 space-y-2" data-testid={`${testid}-link-panel`}>
          <input
            value={linkName}
            onChange={(e) => setLinkName(e.target.value)}
            placeholder="Label (optional)"
            data-testid={`${testid}-link-name`}
            className="w-full rounded-none border border-border px-2 py-1.5 text-sm bg-background"
          />
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://sharepoint… or Drive URL"
            data-testid={`${testid}-link-url`}
            className="w-full rounded-none border border-border px-2 py-1.5 text-sm bg-background"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submitLink}
              disabled={busy}
              data-testid={`${testid}-link-submit`}
              className="flex-1 border border-black bg-primary text-primary-foreground px-2 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              Attach link
            </button>
            <button
              type="button"
              onClick={() => setShowLink(false)}
              className="border border-border px-2 py-1.5 text-xs font-semibold hover:bg-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowLink(true)}
          data-testid={`${testid}-link-toggle`}
          className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"
        >
          <LinkSimple size={13} /> Attach a repository link instead
        </button>
      )}
    </div>
  );
}
